import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useCallback } from "react";

/**
 * Custom React hook to retrieve an access token using MSAL.
 * It attempts silent token acquisition and falls back to interactive login if needed.
 *
 * @returns {Object} - An object containing the getAccessToken function.
 */
export const useAccessToken = () => {
  const { instance, accounts } = useMsal();

  /**
   * Retrieves an access token for the given scopes.
   *
   * @param {Array<string>} scopes - The scopes for which the token is requested. Defaults to ["User.Read"].
   * @param {string} loginMethod - The fallback login method: "redirect" or "popup". Defaults to "redirect".
   * @returns {Promise<string>} - A promise that resolves to the access token.
   */
  const getAccessToken = useCallback(
    async (scopes = ["User.Read"], loginMethod = "redirect") => {
      if (accounts.length === 0) {
        throw new Error("No accounts found. Ensure the user is logged in.");
      }

      try {
        // Attempt silent token acquisition
        const response = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes,
        });

        return response.accessToken;
      } catch (error) {
        // Handle cases where user interaction is required
        if (error instanceof InteractionRequiredAuthError) {
          console.warn("Silent token acquisition failed. Initiating login...");

          if (loginMethod === "popup") {
            await instance.loginPopup({ scopes });
          } else {
            await instance.loginRedirect({ scopes });
          }
        } else {
          // Log and rethrow unexpected errors
          console.error("Access token error:", error);
          throw error;
        }
      }
    },
    [accounts, instance]
  );

  return { getAccessToken };
};
